'use client'
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { GlobalTypography } from 'cms-types'
import { MouseEvent, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Order } from 'shopify-types'
import { printMoney } from 'shopify-types/helpers'

import Icon from '../../components/Icon'
import { formatReviewDate } from '../../helpers/formatReviews'
import { useWindowSize } from '../../hooks/useWindowSize'

type LanguageModalProps = {
  showModal: boolean
  onClose: () => void
  order: Order | null
  typography: GlobalTypography['authSection']
  locale: string
}

function AccountOrdersModal({ onClose, showModal, order, typography, locale }: LanguageModalProps) {
  const modalRef = useRef<HTMLDialogElement>(null)
  const [closeAnimationPlaying, setCloseAnimationPlaying] = useState(false)
  const { isDesktop } = useWindowSize()

  useEffect(() => {
    if (showModal && modalRef.current !== null) {
      modalRef.current.showModal()
      modalRef.current.classList.add(isDesktop ? 'openDownToUp' : 'openRightToLeft')
    }
  }, [isDesktop, showModal])

  const onModalCloseClick = useCallback(() => {
    modalRef?.current?.classList.add(isDesktop ? 'closeDownToUp' : 'closeRightToLeft')

    setCloseAnimationPlaying(true)
  }, [isDesktop])

  const onEscapeClose = useCallback(
    (e: SyntheticEvent<HTMLDialogElement>) => {
      e.preventDefault()
      onModalCloseClick()
    },
    [onModalCloseClick],
  )

  const onModalClick = useCallback(
    (event: MouseEvent<HTMLDialogElement>) => {
      if (!modalRef.current) return

      // check if we click on backdrop
      const rect = modalRef.current.getBoundingClientRect()

      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        onModalCloseClick()
      }
    },
    [onModalCloseClick],
  )

  const onCloseAnimationEnd = useCallback(() => {
    if (!modalRef.current || !closeAnimationPlaying) return

    modalRef.current.close()
    setCloseAnimationPlaying(false)

    onClose()
  }, [closeAnimationPlaying, onClose])

  return (
    <dialog
      ref={modalRef}
      onAnimationEnd={onCloseAnimationEnd}
      className='fixed m-auto h-full max-h-full w-full max-w-full bg-lightgray backdrop:bg-[#00000080] xl:m-auto xl:h-fit xl:max-h-[90%] xl:max-w-[90%]'
      onClick={onModalClick}
      onCancel={onEscapeClose}
    >
      <div className='relative flex flex-col rounded-md bg-lightgray p-5 text-left xl:p-10'>
        <button className='absolute right-6 top-6 ml-auto' onClick={onModalCloseClick} autoFocus>
          <Icon name='MdClose' size={20} />
        </button>

        <h5 className='mb-10 mt-4'>
          {typography.ordersTab.orderIdColumnTitle} {order?.name}
        </h5>

        <table>
          <colgroup>
            <col className='w-3/6' />
            <col className='w-1/6' />
            <col className='w-1/6' />
            <col className='w-1/6' />
          </colgroup>

          <thead>
            <tr>
              <th className='px-1 py-2'>
                <p className='xs font-bold'>{typography.ordersTab.orderTitleColumnTitle}</p>
              </th>

              <th className='px-1 py-2'>
                <p className='xs text-center font-bold'>
                  {typography.ordersTab.orderPriceColumnTitle}
                </p>
              </th>

              <th className='px-1 py-2'>
                <p className='xs text-center font-bold'>
                  {typography.ordersTab.orderQuantityColumnTitle}
                </p>
              </th>

              <th className='px-1 py-2'>
                <p className='xs text-center font-bold'>
                  {typography.ordersTab.orderTotalColumnTitle}
                </p>
              </th>
            </tr>
          </thead>

          <tbody className='xl:max-h-[315px] xl:overflow-y-scroll'>
            {order?.lineItems?.map(lineItem => (
              <tr key={lineItem.variant.id}>
                <td className='px-1 py-2'>
                  <p className='xs'>{`${lineItem.title}  ${lineItem.variant.title}`}</p>
                </td>

                <td className='px-1 py-2'>
                  <p className='xs text-center'>{printMoney(lineItem.variant.price)}</p>
                </td>

                <td className='px-1 py-2'>
                  <p className='xs text-center'>{lineItem.quantity}</p>
                </td>

                <td className='px-1 py-2'>
                  <p className='xs text-center'>{printMoney(lineItem.discountedTotalPrice)}</p>
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot className='border-t'>
            <tr>
              <td />
              <td />
              <td />
              <td className=' py-2'>
                <p className='xs text-right xl:text-center'>{printMoney(order?.totalPrice)}</p>
              </td>
            </tr>
          </tfoot>
        </table>

        <div>
          <div>
            <p className='xs'>
              {typography.ordersTab.orderDateColumnTitle}:{' '}
              {formatReviewDate(order?.processedAt || '', locale)}
            </p>
          </div>

          <div>
            <p className='xs'>
              {typography.ordersTab.orderStatusColumnTitle}: {order?.financialStatus}
            </p>
          </div>

          <div>
            <p className='xs'>
              {typography.ordersTab.orderDeliveryColumnTitle}: {order?.fulfillmentStatus}
            </p>
          </div>
        </div>
      </div>
    </dialog>
  )
}

export default AccountOrdersModal
